import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { OpenEnrollmentPeriodDto } from '@zorro/clients';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Menu } from '@zorro/design-system/v2/Menu';
import { Title } from '@zorro/design-system/v2/Title';
import { getNow, parseDateISO } from '@zorro/shared/formatters';
import { Group, Icon, brand } from '@zorro/web/design-system';
import {
  getSelectedOpenEnrollmentPeriodStorageKey,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';

type Props = {
  titlePrefix?: string | null;
  employerId: string;
  onSelect?: (period: OpenEnrollmentPeriodDto) => void;
};

export const PlanYearTitle = ({ titlePrefix, employerId, onSelect }: Props) => {
  const storedOpenEnrollmentPeriodId = readLocalStorageValue<string>({
    key: getSelectedOpenEnrollmentPeriodStorageKey(employerId),
    defaultValue: '',
  });

  const [openEnrollmentPeriodQuery, setOpenEnrollmentPeriodQuery] =
    useQueryState('enrollmentPeriodId', parseAsString.withDefault(''));

  const [, setEnrollmentPeriodLocal] = useLocalStorage({
    key: getSelectedOpenEnrollmentPeriodStorageKey(employerId),
    defaultValue: storedOpenEnrollmentPeriodId,
  });

  const [selectedOpenEnrollmentPeriod, setSelectedOpenEnrollmentPeriod] =
    useState<OpenEnrollmentPeriodDto | null>(null);

  const { data: openEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const selectedPlanYear = selectedOpenEnrollmentPeriod?.effectiveFrom
    ? parseDateISO(selectedOpenEnrollmentPeriod.effectiveFrom).year().toString()
    : null;

  useEffect(() => {
    if (openEnrollmentPeriodQuery) {
      const selectedOEP = openEnrollmentPeriods.find(
        (oep) => oep.id === openEnrollmentPeriodQuery
      );
      if (selectedOEP) {
        setSelectedOpenEnrollmentPeriod(selectedOEP);
        setEnrollmentPeriodLocal(selectedOEP.id);
      }
    } else if (storedOpenEnrollmentPeriodId) {
      const selectedOEP = openEnrollmentPeriods.find(
        (oep) => oep.id === storedOpenEnrollmentPeriodId
      );
      if (selectedOEP) {
        setSelectedOpenEnrollmentPeriod(selectedOEP);
        void setOpenEnrollmentPeriodQuery(selectedOEP.id);
      }
    } else if (
      selectedOpenEnrollmentPeriod === null &&
      openEnrollmentPeriods.length > 0
    ) {
      const currentOEP = openEnrollmentPeriods.find((oep) =>
        getNow().isBetween(
          parseDateISO(oep.effectiveFrom),
          parseDateISO(oep.effectiveUntil)
        )
      );
      if (currentOEP) {
        setSelectedOpenEnrollmentPeriod(currentOEP);
        onSelect?.(currentOEP);
      } else {
        const latestOEP = openEnrollmentPeriods.sort(
          (prev, curr) =>
            parseDateISO(curr.effectiveFrom).year() -
            parseDateISO(prev.effectiveFrom).year()
        )[0];
        setSelectedOpenEnrollmentPeriod(latestOEP);
        onSelect?.(latestOEP);
      }
    }
  }, [
    selectedOpenEnrollmentPeriod,
    openEnrollmentPeriods,
    onSelect,
    storedOpenEnrollmentPeriodId,
    openEnrollmentPeriodQuery,
    setEnrollmentPeriodLocal,
    setOpenEnrollmentPeriodQuery,
  ]);

  return (
    <Group gap="xs">
      <Title>{`${titlePrefix ?? ''}${
        selectedPlanYear ? ` ${selectedPlanYear}` : ''
      }`}</Title>
      {openEnrollmentPeriods.length > 1 && (
        <Menu size="sm" position="bottom-start">
          <Menu.Target>
            <ActionIcon>
              <Icon icon={IconChevronDown} color={brand.zorroGray800} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {openEnrollmentPeriods.map((period) => (
              <Menu.Item
                key={period.id}
                onClick={() => {
                  onSelect?.(period);
                  setSelectedOpenEnrollmentPeriod(period);
                }}
              >
                {parseDateISO(period.effectiveFrom).year()}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
};
